import { useState } from 'react';
import { AnalysisContext } from './AnalysisContext';
import { fetchAnalysisSettingsByJob } from '../../api/Settings/CalculationSettings.api';
import { ResponseData } from '../../api/ApiFetch/ApiResponse';
import {
	AccrualBasis,
	Analysis,
	AnalysisGroup,
	AnalysisGroupValue,
	AnalysisSettingByJob,
	AnalysisSettingsValue,
	Bifurcation,
	CalculationBaseGroup,
	Character,
	ClientType,
	ConstructiveSales,
	Dividend,
	ForcedConstructiveSales,
	Hedging,
	HedgingType,
	IRC475,
	ReliefMethodOptions,
	Reporting,
	MTMSubSetting,
	MTMSubSettingDefault,
} from '../../component/Settings/Settings.types';
import { showErrorSnackbar } from '../../component/snackbar/Snackbar.slice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Messages } from '../../Constants/Messages';
import { Guid } from 'guid-typescript';
import { DefaultCalculationSetting } from '../../component/Settings/DefaultCalculationSetting';
import { RootState } from '../../app/store';
import { set } from 'lodash';

const AnalysisContextProvider = ({ children }: any) => {
	const dispatch = useAppDispatch();
	const analysis = useAppSelector((state: RootState) => state.analysis.analysisDetails.analysis);
	const defaultCalculationSettings = new DefaultCalculationSetting();
	const [isWashSalesOverlappingDays, setIsWashSalesOverlappingDays] = useState<boolean>(false);
	const [analysisSettingsValue, setAnalysisSettingsValue] = useState<string[]>([]);
	const [isWashSalesEnabled, setIsWashSalesEnabled] = useState<boolean>(false);
	const [showDividendOptions, setShowDividendOptions] = useState<boolean>(false);
	const [overrideDividendFactors, setOverrideDividendFactors] = useState<boolean>(true);
	const [cashBasisAdjustment, setCashBasisAdjustment] = useState<boolean>(true);
	const [clientType, setClientType] = useState<string>(ClientType.Investor);
	const [periodStartDate, setPeriodStartDate] = useState<Date>(defaultCalculationSettings.buildStartDate());
	const [periodEndDate, setPeriodEndDate] = useState<Date>(defaultCalculationSettings.buildEndDate());
	const [isDefaultSetting, setIsDefaultSetting] = useState<boolean>(false);
	const [isPaymentEnabled, setIsPaymentEnabled] = useState<boolean>(false);
	const [isStraddlesEnabled, setIsStraddlesEnabled] = useState<boolean>(false);
	const [isForcedConstructiveSales, setIsForcedConstructiveSales] = useState<string>(ForcedConstructiveSales.Yes);
	const [characterRadioCount, setCharacterRadioCount] = useState<string>(Character.SixtyFortySplit);
	const [reliefMethod, setReliefMethod] = useState<string>(ReliefMethodOptions.FIFO);
	const [constructiveSalesChecked, setConstructiveSalesChecked] = useState<boolean>(false);
	const [isHedgingChecked, setIsHedgingChecked] = useState<boolean>(false);
	const [isOneToOneSelected, setIsOneToOneSelected] = useState<boolean>(false);
	const [isSameDaySelected, setIsSameDaySelected] = useState<boolean>(false);
	const [realizedReliefMethod, setRealizedReliefMethod] = useState<string>(ReliefMethodOptions.FIFO);
	const [offsetReliefMethod, setOffsetReliefMethod] = useState<string>(ReliefMethodOptions.FIFO);
	const [hedgingRadioCount, setHedgingRadioCount] = useState<string>(HedgingType.OneToAll);
	const [reportingRadioCount, setReportingRadioCount] = useState<string>(Reporting.Monthly);
	const [bifurcation, setBifurcation] = useState<string>(Bifurcation.Disposition);
	const [accrualBasis, setAccrualBasis] = useState<string>(AccrualBasis.Accrual);
	const [isMTMEnabled, setIsMTMEnabled] = useState<boolean>(false);
	const [mtmSubSettings, setMTMSubSettings] = useState<MTMSubSetting[]>(MTMSubSettingDefault);
	const [savedIsHedgingCheckedValue, setSavedIsHedgingCheckedValue] = useState<boolean>(isHedgingChecked);
	const [savedIsOneToOneSelectedValue, setSavedIsOneToOneSelectedValue] = useState<boolean>(isOneToOneSelected);
	const [is1099or263hSelected, setIs1099or263hSelected] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const setCalculationGroupSettings = (setting: AnalysisSettingByJob) => {
		const settingsValue = JSON.parse(setting.settingValue) as AnalysisGroupValue;
		settingsValue.analysisSetting.forEach((settingsValue: AnalysisSettingsValue) => {
			switch (settingsValue.type) {
				case CalculationBaseGroup.PeriodFrom:
					setPeriodStartDate(new Date(settingsValue.settingValues[0]));
					break;
				case CalculationBaseGroup.PeriodTo:
					setPeriodEndDate(new Date(settingsValue.settingValues[0]));
					break;
				case CalculationBaseGroup.ClientType:
					setClientType(settingsValue.settingValues[0]);
					break;
				case CalculationBaseGroup.Reporting:
					setReportingRadioCount(settingsValue.settingValues[0]);
					break;
				case CalculationBaseGroup.Bifurcation:
					setBifurcation(settingsValue.settingValues[0]);
					break;
				case CalculationBaseGroup.Character:
					setCharacterRadioCount(settingsValue.settingValues[0]);
			}
		});
	};

	const setDefaultSetting = (analysis: Analysis[]) => {
		const analysisIDs: string[] = analysis.map((s) => s.analysisID.toString());
		setAnalysisSettingsValue(analysisIDs);
		setIsWashSalesEnabled(true);
		setShowDividendOptions(true);
		setIsHedgingChecked(true);
		setConstructiveSalesChecked(true);
		setIsPaymentEnabled(true);
		setIsStraddlesEnabled(true);
		setIsDefaultSetting(true);
		setIsMTMEnabled(true);
	};

	const setSettingToDefault = () => {
		setReportingRadioCount(Reporting.Monthly);
		setClientType(ClientType.Investor);
		setPeriodStartDate(defaultCalculationSettings.buildStartDate());
		setPeriodEndDate(defaultCalculationSettings.buildEndDate());
		setIsWashSalesOverlappingDays(true);
		setIsWashSalesEnabled(false);
		setShowDividendOptions(false);
		setOverrideDividendFactors(true);
		setCashBasisAdjustment(true);
		setIsPaymentEnabled(false);
		setIsStraddlesEnabled(false);
		setIsForcedConstructiveSales(ForcedConstructiveSales.No);
		setCharacterRadioCount(Character.SixtyFortySplit);
		setReliefMethod(ReliefMethodOptions.FIFO);
		setConstructiveSalesChecked(false);
		setIsHedgingChecked(false);
		setIsOneToOneSelected(false);
		setIsSameDaySelected(true);
		setRealizedReliefMethod(ReliefMethodOptions.FIFO);
		setOffsetReliefMethod(ReliefMethodOptions.FIFO);
		setHedgingRadioCount(HedgingType.OneToAll);
		setBifurcation(Bifurcation.Disposition);
		setAccrualBasis(AccrualBasis.Accrual);
		setMTMSubSettings(MTMSubSettingDefault);
		setIsMTMEnabled(false);
	};

	const setAnalysisValue = (analysisSettingByJob: AnalysisSettingByJob): AnalysisGroupValue => {
		return JSON.parse(analysisSettingByJob.settingValue) as AnalysisGroupValue;
	};

	const setAnalysisSettingValue = (analysisSettingByJob: AnalysisSettingByJob[]): void => {
		let analysisValue: string[] = [];
		let hedgingGroupFound = false;
		let oneToOneSelected = false;
		analysisSettingByJob.forEach((value: AnalysisSettingByJob) => {
			const settingsValue = JSON.parse(value.settingValue) as AnalysisGroupValue;
			if (settingsValue.analysis) {
				(settingsValue.analysis as string[]).forEach((analysis: string) => {
					analysisValue.push(analysis);
				});
			}
			if (value.analysisGroup === AnalysisGroup.Dividend || value.analysisGroup === AnalysisGroup.Payments) {
				setIs1099or263hSelected(true);
			}
			if (value.analysisGroup === AnalysisGroup.Hedging) {
				hedgingGroupFound = true;
				if (settingsValue.analysisSetting) {
					const oneToOneSetting = settingsValue.analysisSetting.find((setting: any) => setting.settingValues[0] === HedgingType.OneToOneIdentified ||
					setting.settingValues[0] === HedgingType.OneToOneUnidentified);
					if (oneToOneSetting) {
						oneToOneSelected = true;
					}
				}
			}
		});
		setAnalysisSettingsValue(analysisValue);
		setSavedIsHedgingCheckedValue(hedgingGroupFound);
		setSavedIsOneToOneSelectedValue(hedgingGroupFound && oneToOneSelected);
	};

	const setWashSalesGroupSettings = (setting: AnalysisSettingByJob) => {
		const settingsValue = setAnalysisValue(setting);
		setIsWashSalesOverlappingDays(JSON.parse(settingsValue.analysisSetting[0].settingValues[0]));
		setIsWashSalesEnabled(true);
	};

	const setDividendGroupSettings = (setting: AnalysisSettingByJob) => {
		const settingsValue = setAnalysisValue(setting);
		setIs1099or263hSelected(true);
		settingsValue.analysisSetting.forEach((analysisSettingsValue: AnalysisSettingsValue) => {
			switch (analysisSettingsValue.type) {
				case Dividend.OverridDividendFactors:
					setOverrideDividendFactors(JSON.parse(analysisSettingsValue.settingValues[0]));
					break;
				case Dividend.AccrualBasis:
					setAccrualBasis(analysisSettingsValue.settingValues[0]);
					break;
				case Dividend.CashBasisAdjustment:
					setCashBasisAdjustment(JSON.parse(analysisSettingsValue.settingValues[0]));
					break;
				default:
					break;
			}
			setShowDividendOptions(true);
		});
	};

	const setConstructiveSalesAnalysis = (setting: AnalysisSettingByJob) => {
		const settingsValue = setAnalysisValue(setting);
		settingsValue.analysisSetting.forEach((analysisSettingsValue: AnalysisSettingsValue) => {
			switch (analysisSettingsValue.type) {
				case ConstructiveSales.ForcedConstructiveSales:
					setIsForcedConstructiveSales(analysisSettingsValue.settingValues[0]);
					break;
				case ConstructiveSales.ReliefMethodology:
					setReliefMethod(analysisSettingsValue.settingValues[0]);
					break;
				default:
					break;
			}
			setConstructiveSalesChecked(true);
		});
	};

	const setPaymentEnabled = () => {
		setIs1099or263hSelected(true);
		setIsPaymentEnabled(true);
	};

	const setStraddlesEnabled = () => {
		setIsStraddlesEnabled(true);
	};

	const setHedgingAnalysis = (setting: AnalysisSettingByJob) => {
		const settingsValue = setAnalysisValue(setting);
		settingsValue.analysisSetting.forEach((analysisSettingsValue: AnalysisSettingsValue) => {
			switch (analysisSettingsValue.type) {
				case Hedging.HedgingType: {
					const hedgingTypeValue = analysisSettingsValue.settingValues[0] === 'OneToOne'
						? HedgingType.OneToOneUnidentified
						: analysisSettingsValue.settingValues[0];
					setHedgingRadioCount(hedgingTypeValue);
					setIsOneToOneSelected(HedgingType.OneToOneUnidentified === hedgingTypeValue || 
						HedgingType.OneToOneIdentified === hedgingTypeValue);
					break;
				}
				case Hedging.OffsetRelief:
					setOffsetReliefMethod(analysisSettingsValue.settingValues[0]);
					break;
				case Hedging.RealizedRelief:
					setRealizedReliefMethod(analysisSettingsValue.settingValues[0]);
					break;
				case Hedging.SameDayHedging:
					setIsSameDaySelected(JSON.parse(analysisSettingsValue.settingValues[0]));
					break;
				default:
					break;
			}
			setIsHedgingChecked(true);
		});
	};

	const setMTMSettings = (setting: AnalysisSettingByJob) => {
		const settingsValue = setAnalysisValue(setting);
		const mtmSettings: MTMSubSetting[] = [];
		settingsValue.analysisSetting.map((analysisSettingsValue: AnalysisSettingsValue) => {
			const mtmSetting: MTMSubSetting = {
				name: analysisSettingsValue.type,
				value: JSON.parse(analysisSettingsValue.settingValues[0])
			}
			mtmSettings.push(mtmSetting);
		});
		setMTMSubSettings(mtmSettings);
		setIsMTMEnabled(true);
	};

	const setJobSettings = (jobId: Guid) => {
		setIsLoading(true);
		fetchAnalysisSettingsByJob(jobId).then(function (result: ResponseData) {
			if (!result.Success) {
				setSettingToDefault();
				setDefaultSetting(analysis);
				setIsLoading(false);
				showErrorSnackbar(dispatch, Messages.EXCEPTION_FOR_FETCH);
				return;
			}
			const data = JSON.parse(result.Data.toString()) as AnalysisSettingByJob[];
			if (data.length === 0) {
				setSettingToDefault();
				setDefaultSetting(analysis);
				setIsLoading(false);
				return;
			}
			setIsDefaultSetting(false);
			setAnalysisSettingValue(data);
			data.forEach(function (analysisSetting) {
				switch (analysisSetting.analysisGroup) {
					case AnalysisGroup.Dividend:
						setDividendGroupSettings(analysisSetting);
						break;
					case AnalysisGroup.WashSales:
						setWashSalesGroupSettings(analysisSetting);
						break;
					case AnalysisGroup.ConstructiveSales:
						setConstructiveSalesAnalysis(analysisSetting);
						break;
					case AnalysisGroup.Hedging:
						setHedgingAnalysis(analysisSetting);
						break;
					case AnalysisGroup.Calculation:
						setCalculationGroupSettings(analysisSetting);
						break;
					case AnalysisGroup.Payments:
						setPaymentEnabled();
						break;
					case AnalysisGroup.Straddles:
						setStraddlesEnabled();
						break;
					case AnalysisGroup.MTM:
						setMTMSettings(analysisSetting);
						break;
				}
				setIsLoading(false);
			});
		});
	};

	return (
		<AnalysisContext.Provider
			value={{
				setJobSettings,
				isWashSalesOverlappingDays,
				setIsWashSalesOverlappingDays,
				analysisSettingsValue,
				setAnalysisSettingsValue,
				isWashSalesEnabled,
				setIsWashSalesEnabled,
				showDividendOptions,
				setShowDividendOptions,
				overrideDividendFactors,
				setOverrideDividendFactors,
				cashBasisAdjustment,
				setCashBasisAdjustment,
				clientType,
				setClientType,
				periodStartDate,
				setPeriodStartDate,
				periodEndDate,
				setPeriodEndDate,
				isDefaultSetting,
				setIsDefaultSetting,
				isForcedConstructiveSales: isForcedConstructiveSales,
				setIsForcedConstructiveSales: setIsForcedConstructiveSales,
				constructiveSalesChecked,
				setConstructiveSalesChecked,
				isHedgingChecked: isHedgingChecked,
				setIsHedgingChecked: setIsHedgingChecked,
				isOneToOneSelected,
				setIsOneToOneSelected,
				characterRadioCount,
				setCharacterRadioCount,
				reliefMethod,
				setReliefMethod,
				hedgingRadioCount: hedgingRadioCount,
				setHedgingRadioCount: setHedgingRadioCount,
				reportingRadioCount,
				setReportingRadioCount,
				realizedReliefMethod,
				setRealizedReliefMethod,
				offsetReliefMethod,
				setOffsetReliefMethod,
				isPaymentEnabled,
				setIsPaymentEnabled,
				isStraddlesEnabled,
				setIsStraddlesEnabled,
				bifurcation,
				setBifurcation,
				accrualBasis,
				setAccrualBasis,
				isMTMEnabled,
				setIsMTMEnabled,
				mtmSubSettings,
				setMTMSubSettings,
				isSameDaySelected,
				setIsSameDaySelected,
				savedIsHedgingCheckedValue,
				setSavedIsHedgingCheckedValue,
				savedIsOneToOneSelectedValue,
				setSavedIsOneToOneSelectedValue,
				is1099or263hSelected,
				setIs1099or263hSelected,
				isLoading,
				setIsLoading,
			}}
		>
			{children}
		</AnalysisContext.Provider>
	);
};

export default AnalysisContextProvider;
